<template>
  <div class="deploys-page">
    <div class="deploys-header">
      <h4 class="deploys-title">Deploys</h4>
      <p class="deploys-subtitle">
        Chamados aguardando deploy — dispare o pipeline direto por aqui.
      </p>
    </div>

    <div class="deploys-card">
      <div class="deploys-toolbar">
        <div class="deploys-search">
          <i class="fas fa-search"></i>
          <input
            type="text"
            class="form-control"
            placeholder="Buscar por título ou nº do chamado"
            v-model="search"
          />
        </div>
        <div class="deploys-toolbar-right">
          <div class="deploys-stat">
            <span class="deploys-stat-icon"><i class="fas fa-cloud-upload-alt"></i></span>
            <div class="deploys-stat-info">
              <span class="deploys-stat-number">{{ totalCount }}</span>
              <span class="deploys-stat-label">aguardando deploy</span>
            </div>
          </div>
          <button class="btn-refresh" :disabled="loading" @click="fetchDeploys">
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            Atualizar
          </button>
        </div>
      </div>

      <div v-if="loading" class="deploys-state">
        <i class="fas fa-circle-notch fa-spin"></i>
        Carregando chamados...
      </div>

      <div v-else-if="calls.length === 0" class="deploys-state">
        <i class="fas fa-check-circle deploys-state-icon"></i>
        <p>Nenhum chamado aguardando deploy.</p>
      </div>

      <div v-else class="table-responsive">
        <table class="deploys-table">
          <thead>
            <tr>
              <th>Chamado</th>
              <th>Sistema</th>
              <th>Solicitante</th>
              <th>Responsável</th>
              <th>Aberto em</th>
              <th>Aguardando</th>
              <th class="text-center">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="call in calls" :key="call.id">
              <td>
                <span class="deploys-call-id">#{{ call.id }}</span>
                <span class="deploys-call-title">{{ call.title }}</span>
              </td>
              <td>{{ systemName(call) }}</td>
              <td>{{ requesterName(call) }}</td>
              <td>{{ responsibleName(call) }}</td>
              <td>{{ call.created_at_f || "-" }}</td>
              <td>
                <span
                  class="deploys-dot"
                  :class="waitingInfo(call).className"
                ></span>
                {{ waitingInfo(call).label }}
              </td>
              <td class="text-center">
                <div class="deploys-actions">
                  <button
                    class="btn-icon-view"
                    title="Visualizar chamado"
                    @click="viewDeploy(call)"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button class="btn-run-pipeline" @click="runPipeline(call)">
                    <i class="fas fa-rocket"></i>
                    Rodar Pipeline
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalCount > perPage" class="deploys-paginator">
        <small class="text-muted">
          Página {{ page }} de {{ lastPage }} — {{ totalCount }} chamado(s)
        </small>
        <div class="deploys-paginator-actions">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page <= 1 || loading"
            @click="goToPage(page - 1)"
          >
            ‹ Anterior
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page >= lastPage || loading"
            @click="goToPage(page + 1)"
          >
            Próxima ›
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const DEPLOY_STATUS = 18;

export default {
  data() {
    return {
      calls: [],
      loading: false,
      search: "",
      searchTimeout: null,
      page: 1,
      perPage: 15,
      totalCount: 0,
      lastPage: 1,
    };
  },
  mounted() {
    this.fetchDeploys();
  },
  watch: {
    search() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.page = 1;
        this.fetchDeploys();
      }, 500);
    },
  },
  methods: {
    async fetchDeploys() {
      this.loading = true;
      try {
        const api = this.$store.state.api + "calls-deploys";
        const params = {
          orderByDesc: "id",
          status: DEPLOY_STATUS,
          limit: this.perPage,
          page: this.page,
          with: ["dev", "status_calls", "user_model", "project"],
        };
        if (this.search) {
          params.title = this.search;
        }

        const res = await axios.get(api, { params });

        this.calls = res.data.data || [];
        this.totalCount = res.data.count || 0;
        this.lastPage = res.data.pages || 1;
      } catch (e) {
        this.$message(
          "Ops",
          "Não foi possível carregar os chamados aguardando deploy.",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page < 1 || page > this.lastPage || page === this.page) return;
      this.page = page;
      this.fetchDeploys();
    },
    requesterName(call) {
      return call.user_model ? call.user_model.name : "-";
    },
    responsibleName(call) {
      return call.dev ? call.dev.name : "-";
    },
    systemName(call) {
      return call.project ? call.project.name : "-";
    },
    waitingInfo(call) {
      const reference = call.updated_at || call.created_at;
      if (!reference) return { label: "-", className: "" };

      const days = Math.floor(
        (Date.now() - new Date(reference).getTime()) / 86400000
      );
      const label = days <= 0 ? "hoje" : `há ${days}d`;

      if (days <= 2) return { label, className: "dot-green" };
      if (days <= 5) return { label, className: "dot-yellow" };
      return { label, className: "dot-red" };
    },
    viewDeploy(call) {
      const url = `${window.location.origin}/calls/ticket/${call.id}`;
      window.open(url, "_blank");
    },
    runPipeline(call) {
      // Integração com o Jenkins ainda não configurada — placeholder até o backend expor o endpoint de trigger.
      this.$message(
        "Em breve",
        "A integração com o Jenkins ainda está sendo configurada.",
        "info"
      );
    },
  },
};
</script>

<style scoped>
.deploys-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.deploys-title {
  font-weight: 700;
  color: #15488a;
  margin-bottom: 2px;
}

.deploys-subtitle {
  color: #6e6b7b;
  margin-bottom: 0;
  font-size: 0.9rem;
}

.deploys-stat {
  background: #fff9e6;
  border: 1px solid #f1c21b;
  border-radius: 8px;
  padding: 7px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.deploys-stat-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #f1c21b;
  color: #4a3c00;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.deploys-stat-info {
  display: flex;
  flex-direction: column;
}

.deploys-stat-number {
  font-size: 1.35rem;
  font-weight: 700;
  color: #4a3c00;
  line-height: 1;
}

.deploys-stat-label {
  font-size: 0.72rem;
  color: #8a6d00;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
}

.deploys-card {
  background: #fff;
  border: 1px solid #d6d6d6;
  border-radius: 8px;
  padding: 20px;
}

.deploys-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.deploys-toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.deploys-search {
  position: relative;
  flex: 1;
  min-width: 240px;
  max-width: 380px;
}

.deploys-search i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #b9b9c3;
  font-size: 0.85rem;
}

.deploys-search .form-control {
  padding-left: 34px;
  border-radius: 6px;
}

.btn-refresh {
  border: 2px solid #15488a;
  background: #fff;
  color: #15488a;
  border-radius: 0.25rem;
  font-weight: 600;
  padding: 6px 16px;
  font-size: 0.85rem;
}

.btn-refresh:hover:not(:disabled) {
  background: #15488a;
  color: #fff;
}

.btn-refresh:disabled {
  opacity: 0.6;
}

.deploys-state {
  text-align: center;
  padding: 48px 0;
  color: #6e6b7b;
}

.deploys-state-icon {
  font-size: 1.8rem;
  color: #24a148;
  display: block;
  margin-bottom: 10px;
}

.deploys-table {
  width: 100%;
  border-collapse: collapse;
}

.deploys-table thead th {
  text-align: left;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
  border-bottom: 2px solid #ebe9f1;
  padding: 10px 12px;
}

.deploys-table tbody td {
  padding: 14px 12px;
  border-bottom: 1px solid #ebe9f1;
  vertical-align: middle;
  font-size: 0.9rem;
}

.deploys-table tbody tr:hover {
  background: #f8f8fb;
}

.deploys-call-id {
  display: block;
  font-weight: 700;
  color: #15488a;
  font-size: 0.8rem;
}

.deploys-call-title {
  display: block;
  color: #5e5873;
}

.deploys-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 6px;
}

.deploys-dot.dot-green {
  background: #24a148;
}

.deploys-dot.dot-yellow {
  background: #f1c21b;
}

.deploys-dot.dot-red {
  background: #da1e28;
}

.deploys-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-icon-view {
  width: 32px;
  height: 32px;
  border: 2px solid #15488a;
  border-radius: 6px;
  background: #fff;
  color: #15488a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-icon-view:hover {
  background: #15488a;
  color: #fff;
}

.btn-run-pipeline {
  border: none;
  background: #f1c21b;
  color: #4a3c00;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 8px 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.btn-run-pipeline:hover {
  background: #d9ac0b;
}

.deploys-paginator {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.deploys-paginator-actions {
  display: flex;
  gap: 8px;
}

/* Modo dark */
body.dark-layout .deploys-title {
  color: #5a9bf0;
}

body.dark-layout .deploys-subtitle {
  color: #b4b7bd;
}

body.dark-layout .deploys-card {
  background: #283046;
  border-color: #404656;
}

body.dark-layout .deploys-stat {
  background: rgba(241, 194, 27, 0.12);
}

body.dark-layout .deploys-stat-number {
  color: #f1c21b;
}

body.dark-layout .deploys-stat-label {
  color: #d9ac0b;
}

body.dark-layout .btn-refresh {
  background: transparent;
  border-color: #5a9bf0;
  color: #5a9bf0;
}

body.dark-layout .btn-refresh:hover:not(:disabled) {
  background: #5a9bf0;
  color: #161d31;
}

body.dark-layout .deploys-state {
  color: #b4b7bd;
}

body.dark-layout .deploys-table thead th {
  color: #b4b7bd;
  border-bottom-color: #404656;
}

body.dark-layout .deploys-table tbody td {
  border-bottom-color: #404656;
}

body.dark-layout .deploys-table tbody tr:hover {
  background: #232a3d;
}

body.dark-layout .deploys-call-id {
  color: #5a9bf0;
}

body.dark-layout .deploys-call-title {
  color: #b4b7bd;
}

body.dark-layout .btn-icon-view {
  background: transparent;
  border-color: #5a9bf0;
  color: #5a9bf0;
}

body.dark-layout .btn-icon-view:hover {
  background: #5a9bf0;
  color: #161d31;
}
</style>
